import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHomeContent } from "../hooks/useHomeContent";
import Masthead from "../components/layout/Masthead";
import { MediaSection } from "../components/media/MediaSection";
import TopicExplorer from "../components/topics/TopicExplorer";
import { useTheme } from "../contexts/ThemeContext";
import { useTopics } from "../hooks/useTopics";
import SEO from "../components/common/SEO";

const Home: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { topics } = useTopics();
  const {
    shorts,
    isShortsLoading,
    shortsError,
    regularVideos,
    isRegularLoading,
    regularError,
    topicSections,
    selectedTopics,
    setSelectedTopics,
  } = useHomeContent();

  const handleTopicSelect = useCallback(
    (topicIds: string[]) => {
      setSelectedTopics(topicIds);
    },
    [setSelectedTopics]
  );

  return (
    <div className="min-h-screen">
      <SEO
        title={t('navigation.home')}
        description={t('meta.homeDescription')}
        keywords={[
          'Jewish content',
          'Jewish videos',
          'Torah lessons',
          'Jewish education',
          'Jewish shorts',
          'Jewish wisdom',
          'Torah study'
        ]}
      />
      <Masthead />

      <main className="bg-background">
        {/* Featured Content */}
        <div className="container mx-auto lg:max-w-7xl px-4 lg:px-8 overflow-visible pt-8 sm:pt-16">
          {/* Trending Shorts Section */}
          <MediaSection
            title={t("content.trendingShorts")}
            items={shorts}
            type="shorts"
            loading={isShortsLoading}
            error={shortsError}
          />
        </div>

        {/* Separator Line */}
        <div className="w-full my-8 sm:my-16">
          <div className="h-px bg-text-secondary opacity-20" />
        </div>

        <div className="container mx-auto lg:max-w-7xl px-4 lg:px-8 overflow-visible">
          {/* New Videos Section */}
          <MediaSection
            title={t("content.newVideos")}
            items={regularVideos}
            type="video"
            loading={isRegularLoading}
            error={regularError}
          />
        </div>

        {/* Topic Explorer Section */}
        <section
          className="relative mt-8 sm:mt-16 pb-4"
          style={{
            backgroundColor: "rgba(var(--colors-primary-500-rgb), 0.05)",
          }}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/10 to-transparent" />

          <div className="container mx-auto lg:max-w-[calc(80rem+8rem)] px-4 lg:px-16">
            <div className="py-8 sm:py-16">
              <TopicExplorer
                topics={topics}
                onTopicSelect={handleTopicSelect}
                selectedTopics={selectedTopics}
                topicSections={topicSections}
              />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
